/**
 * Coarse grocery-aisle categorization for shopping-list grouping.
 *
 * Pure keyword matching against the ingredient's `item` text. Returns
 * one of a fixed set of buckets so the UI can group lines under stable
 * headings ("Produce", "Dairy", etc.). Falls back to "other" when no
 * keyword matches — that bucket is rendered last in the UI.
 *
 * Order matters: more-specific buckets are checked before broader ones
 * (e.g. "coconut milk" -> dairy, not produce; "vanilla extract" -> baking,
 * not pantry).
 */

export type IngredientCategory =
  | "produce"
  | "meat"
  | "seafood"
  | "dairy"
  | "bakery"
  | "baking"
  | "spices"
  | "pantry"
  | "frozen"
  | "beverage"
  | "other";

export const INGREDIENT_CATEGORY_LABELS: Record<IngredientCategory, string> = {
  produce: "Produce",
  meat: "Meat",
  seafood: "Seafood",
  dairy: "Dairy & Eggs",
  bakery: "Bakery",
  baking: "Baking",
  spices: "Spices & Herbs",
  pantry: "Pantry",
  frozen: "Frozen",
  beverage: "Beverages",
  other: "Other",
};

// UI display order: aisles you typically walk through first to last.
export const INGREDIENT_CATEGORY_ORDER: IngredientCategory[] = [
  "produce",
  "meat",
  "seafood",
  "dairy",
  "bakery",
  "baking",
  "spices",
  "pantry",
  "frozen",
  "beverage",
  "other",
];

interface CategoryRule {
  category: IngredientCategory;
  // Whole-word keyword matched after lowercasing the item text.
  keywords: string[];
}

const RULES: CategoryRule[] = [
  // Spices & herbs first — many spice names contain "salt" / "pepper"
  // which would otherwise be miscategorized.
  {
    category: "spices",
    keywords: [
      "salt", "pepper", "peppercorn", "cinnamon", "nutmeg", "clove", "cloves",
      "paprika", "cumin", "coriander", "turmeric", "saffron", "cardamom",
      "oregano", "basil", "thyme", "rosemary", "sage", "tarragon", "dill",
      "parsley", "cilantro", "chives", "mint", "bay leaf", "bay leaves",
      "allspice", "ginger powder", "garlic powder", "onion powder",
      "chili powder", "cayenne", "curry powder", "five spice", "italian seasoning",
      "herbs", "herb", "spice", "spices", "seasoning", "vanilla extract",
      "almond extract",
    ],
  },
  // Baking — specific items before general "flour"/"sugar"
  {
    category: "baking",
    keywords: [
      "flour", "sugar", "brown sugar", "powdered sugar", "confectioners",
      "yeast", "baking powder", "baking soda", "cornstarch", "cornmeal",
      "cocoa", "chocolate chip", "chocolate chips", "sprinkles", "shortening",
      "molasses", "honey", "maple syrup", "corn syrup", "extract",
      "food coloring", "icing",
    ],
  },
  // Dairy & eggs
  {
    category: "dairy",
    keywords: [
      "milk", "cream", "butter", "cheese", "yogurt", "yoghurt", "sour cream",
      "cream cheese", "ricotta", "mozzarella", "parmesan", "cheddar", "feta",
      "egg", "eggs", "half and half", "buttermilk", "ghee", "creme fraiche",
    ],
  },
  // Bakery (after dairy so "bread crumbs" land right)
  {
    category: "bakery",
    keywords: [
      "bread", "baguette", "bun", "buns", "roll", "rolls", "tortilla",
      "tortillas", "pita", "naan", "bagel", "croissant", "english muffin",
      "muffin", "cake", "pie crust", "puff pastry", "phyllo", "biscuit",
    ],
  },
  // Frozen
  {
    category: "frozen",
    keywords: ["frozen", "ice cream", "popsicle", "sorbet"],
  },
  // Seafood (before meat)
  {
    category: "seafood",
    keywords: [
      "fish", "salmon", "tuna", "cod", "tilapia", "trout", "halibut", "snapper",
      "shrimp", "prawn", "crab", "lobster", "scallop", "scallops", "oyster",
      "clam", "clams", "mussel", "mussels", "anchovy", "anchovies", "sardine",
      "sardines",
    ],
  },
  // Meat & poultry
  {
    category: "meat",
    keywords: [
      "chicken", "beef", "pork", "lamb", "veal", "turkey", "duck", "ham",
      "bacon", "sausage", "ground beef", "ground turkey", "ground pork",
      "steak", "ribs", "brisket", "tenderloin", "chop", "chops", "roast",
      "meatball", "meatballs", "pepperoni", "salami", "prosciutto",
      "hot dog", "hot dogs",
    ],
  },
  // Beverages
  {
    category: "beverage",
    keywords: [
      "wine", "beer", "vodka", "rum", "whiskey", "whisky", "bourbon", "gin",
      "tequila", "brandy", "liqueur", "champagne", "sake", "vermouth",
      "juice", "soda", "coffee", "tea", "espresso", "broth", "stock",
    ],
  },
  // Produce — fruit & veg (broad)
  {
    category: "produce",
    keywords: [
      "onion", "garlic", "shallot", "leek", "scallion", "green onion",
      "carrot", "celery", "potato", "sweet potato", "yam", "tomato",
      "cucumber", "zucchini", "squash", "pumpkin", "eggplant", "bell pepper",
      "pepper", "jalapeno", "chili", "chile", "broccoli", "cauliflower",
      "cabbage", "kale", "spinach", "lettuce", "arugula", "chard", "collard",
      "asparagus", "artichoke", "brussels sprout", "mushroom", "corn",
      "peas", "green bean", "bean sprout", "radish", "beet", "turnip",
      "parsnip", "fennel", "okra", "avocado",
      "apple", "banana", "orange", "lemon", "lime", "grape", "berry",
      "strawberry", "blueberry", "raspberry", "blackberry", "cherry",
      "peach", "pear", "plum", "mango", "pineapple", "watermelon", "melon",
      "cantaloupe", "kiwi", "papaya", "pomegranate", "fig", "date",
      "raisin", "raisins", "cranberry",
    ],
  },
  // Pantry — catch-all dry/canned/oils last
  {
    category: "pantry",
    keywords: [
      "oil", "olive oil", "vegetable oil", "canola oil", "coconut oil",
      "vinegar", "soy sauce", "fish sauce", "worcestershire", "hot sauce",
      "ketchup", "mustard", "mayonnaise", "mayo", "salsa", "sriracha",
      "pasta", "spaghetti", "noodle", "noodles", "rice", "quinoa", "couscous",
      "barley", "oats", "oatmeal", "lentil", "lentils", "chickpea",
      "chickpeas", "garbanzo", "kidney bean", "black bean", "white bean",
      "pinto bean", "tofu", "tempeh", "tahini", "peanut butter", "almond butter",
      "jam", "jelly", "preserves", "marmalade", "syrup", "stock", "broth",
      "tomato sauce", "tomato paste", "diced tomato", "crushed tomato",
      "coconut milk", "coconut cream", "evaporated milk", "condensed milk",
      "almond milk", "soy milk", "oat milk",
      "almond", "walnut", "pecan", "cashew", "pistachio", "hazelnut", "peanut",
      "pine nut", "sunflower seed", "sesame seed", "pumpkin seed",
      "cracker", "crackers", "chip", "chips", "cereal", "granola",
    ],
  },
];

/**
 * Categorize an ingredient line by its `item` text. Case-insensitive
 * substring match using whole-word boundaries where reasonable.
 */
export function categorizeIngredient(item: string): IngredientCategory {
  const text = (item ?? "").toLowerCase();
  if (!text.trim()) return "other";

  for (const rule of RULES) {
    for (const kw of rule.keywords) {
      // Use word-boundary regex for short keywords to avoid false positives
      // ("egg" matching "eggplant"), but plain substring for multi-word
      // keywords ("bay leaf") since \b doesn't behave well with spaces.
      if (kw.includes(" ")) {
        if (text.includes(kw)) return rule.category;
      } else {
        const re = new RegExp(`\\b${escapeReg(kw)}\\b`);
        if (re.test(text)) return rule.category;
      }
    }
  }

  return "other";
}

function escapeReg(s: string): string {
  return s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}
